import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Sparkles } from "lucide-react";
import founderImage from "../assets/founder.jpg.jpeg";


export const Founder = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    const el = document.getElementById("lead-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="founder" data-testid="founder-section" className="py-24 relative overflow-hidden">
      <div className="section-divider mb-24 max-w-7xl mx-auto" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image on the left */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[radial-gradient(closest-side,rgba(212,175,55,0.22),transparent)] blur-2xl pointer-events-none" />

            <div className="relative rounded-3xl overflow-hidden ring-1 ring-[#D4AF37]/40 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
              <img
                src={founderImage}
                alt="Sushil Pinjani — Founder of VR Marketing Hub"
                data-testid="founder-image"
                loading="lazy"
                className="w-full h-full object-cover aspect-[4/5]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 via-transparent to-transparent pointer-events-none" />

              {/* Floating premium badge on the image */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35, duration: 0.6 }}
                className="absolute bottom-4 left-4 right-4 flex items-center gap-3 px-4 py-3 rounded-2xl glass"
              >
                <div className="w-9 h-9 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] shrink-0">
                  <BadgeCheck size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">Sushil Pinjani</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] truncate">
                    Founder · VR Marketing Hub
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Text on the right */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-3">
              Meet the Founder
            </div>

            <div
              data-testid="founder-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 mb-6"
            >
              <Sparkles size={12} className="text-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                Founder · VR Marketing Hub
              </span>
            </div>

            <h2
              data-testid="founder-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-white"
            >
              Hi, I'm <span className="gold-gradient-text">Sushil Pinjani</span>
            </h2>
            <div className="mt-3 text-base sm:text-lg text-white/70">
              Founder of VR Marketing Hub
            </div>

            <p className="mt-6 text-white/70 leading-relaxed text-[15px] sm:text-base">
              I started VR Marketing Hub with one simple mission — help Indian businesses grow with clarity, honesty
              and modern AI-powered marketing. From local shops to growing D2C brands, we build lead engines that
              actually deliver — not vanity metrics.
            </p>
            <p className="mt-4 text-white/70 leading-relaxed text-[15px] sm:text-base">
              Every strategy is personal. Every campaign is transparent. And every client gets a founder-led
              partnership that treats your business the way we treat our own.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#lead-form"
                onClick={scrollToForm}
                data-testid="founder-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#050B14] text-sm font-semibold transition-all shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)]"
              >
                Book FREE Consultation
                <ArrowRight size={16} />
              </a>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "Founder-Led", v: "Service" },
                { k: "AI-Powered", v: "Strategy" },
                { k: "Made in", v: "India" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="text-sm font-bold text-white">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
